import { useCallback, useEffect, useRef } from 'react';

/** How long the search box waits after the last keystroke before it queries. */
export const SEARCH_DEBOUNCE_MS = 300;

/**
 * Returns a function that runs `callback` once calls stop for `delayMs`. The callback that runs is
 * the latest render's, so it reads the state as it is when it fires, not when it was scheduled.
 * A pending call is dropped on unmount.
 */
export function useDebouncedCallback(callback: () => void, delayMs: number) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      callbackRef.current();
    }, delayMs);
  }, [delayMs]);
}
